import React, { useState } from 'react';
import { DateTime } from 'luxon';
import { usePermanentEmployees, useLeaveTypes, useEmployeeLeaves, usePublicHolidays } from '../hooks/useSwr';
import './LeaveCalendar.scss';
import { IonIcon } from '@ionic/react';
import { arrowBack, arrowForward, download, today } from 'ionicons/icons';
import { upperCase } from 'lodash';

const LeaveCalendar: React.FC = () => {
    const { data: permanentEmployees } = usePermanentEmployees();
    const [date, setDate] = useState<DateTime<true>>(DateTime.now());
    const { data: leaveTypes } = useLeaveTypes();
    const { data: leaves } = useEmployeeLeaves(date);
    const { data: publicHolidays } = usePublicHolidays(date);

    const days = Array.from({ length: date.daysInMonth }, (_, i) => i + 1);

    const getLeaveForDay = (employeeId: number, day: number) => {
        const currentDate = date.set({ day }).startOf('day');

        return leaves
            .filter((leave) => leave.employee_id[0] === employeeId)
            .find((leave) => {
                const leaveStartDate = DateTime.fromISO(leave.request_date_from).startOf('day');
                const leaveEndDate = DateTime.fromISO(leave.request_date_to).startOf('day');

                return currentDate >= leaveStartDate && currentDate <= leaveEndDate;
            });
    };

    const getLeaveTypeColor = (typeId: number) => {
        const leaveType = leaveTypes.find((type) => type.id === typeId);
        return leaveType?.calendar_color || '#607d8b'; // Couleur par défaut si non trouvé
    };

    const getLeaveTypeLabel = (typeId: number) => {
        const leaveType = leaveTypes.find((type) => type.id === typeId);
        return leaveType?.calendar_abbreviation || '??';
    };

    const getLeaveTypeFullLabel = (typeId: number) => {
        const leaveType = leaveTypes.find((type) => type.id === typeId);
        return leaveType?.name || '';
    };

    const isWeekend = (day: number) => {
        const currentDate = date.set({ day });
        const dayOfWeek = currentDate.weekday;
        return dayOfWeek === 6 || dayOfWeek === 7; // 6 = samedi, 7 = dimanche
    };

    const isPublicHoliday = (day: number) => {
        const currentDate = date.set({ day });
        return publicHolidays.some((holiday) => holiday.date.hasSame(currentDate, 'day'));
    };

    const getPublicHolidayName = (day: number) => {
        const currentDate = date.set({ day });
        return publicHolidays.find((holiday) => holiday.date.hasSame(currentDate, 'day'))?.name;
    };

    const calculateEmployeeStats = (employeeId: number) => {
        const stats: { [key: string]: number } = {};

        // Initialize counters for each type of leave
        Object.values(leaveTypes).forEach((type) => {
            stats[type.calendar_abbreviation] = 0;
        });

        for (const leave of leaves.filter((l) => l.employee_id[0] === employeeId)) {
            const leaveType = leaveTypes.find((type) => type.id === leave.holiday_status_id[0]);

            if (!leaveType) {
                continue;
            }

            const start = DateTime.fromISO(leave.request_date_from).startOf('day');
            const end = DateTime.fromISO(leave.request_date_to).startOf('day');

            const monthStart = date.startOf('month');
            const monthEnd = date.endOf('month');

            const effectiveStart = start < monthStart ? monthStart : start;
            const effectiveEnd = end > monthEnd ? monthEnd : end;

            if (effectiveStart <= monthEnd && effectiveEnd >= monthStart) {
                const diffInDays = Math.floor(effectiveEnd.diff(effectiveStart, 'days').days) + 1;
                // Add 0.5 instead of 1 for half-days
                stats[leaveType.calendar_abbreviation] += leave.request_unit_half ? 0.5 * diffInDays : diffInDays;
            }
        }

        return stats;
    };

    const calculateTotalStats = (employees: any[]) => {
        const totals: { [key: string]: number } = {};

        // Initialize totals
        Object.values(leaveTypes).forEach((type) => {
            totals[type.calendar_abbreviation] = 0;
        });

        for (const employee of employees) {
            const employeeStats = calculateEmployeeStats(employee.id);

            for (const [code, count] of Object.entries(employeeStats)) {
                totals[code] += count;
            }
        }

        return totals;
    };

    return (
        <div className="planning-container">
            <ToolbarCustom date={date} setDate={setDate} />
            <div className="planning-grid">
                <table>
                    <thead>
                        {/* Month Banner */}
                        <tr>
                            <th colSpan={days.length + 2} className="month-header">
                                {upperCase(date.toFormat('MMMM yyyy'))}
                            </th>
                        </tr>
                        {/* Header with day numbers */}
                        <tr className="planning-header">
                            <th className="employee-column">Employés</th>
                            {days.map((day) => (
                                <th key={day} className={`day-column ${isWeekend(day) ? 'weekend' : ''}`}>
                                    {day}
                                </th>
                            ))}
                            <th className="stats-column">
                                <div className="stats-header">
                                    {leaveTypes.map((type) => (
                                        <div key={type.id} className={`stat-type leave-type-${type.calendar_color}`}>
                                            {type.calendar_abbreviation}
                                        </div>
                                    ))}
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {/* Employee Rows */}
                        {permanentEmployees.map((employee) => (
                            <tr key={employee.id} className="employee-row">
                                <td className="employee-name">{employee.name}</td>
                                {days.map((day) => {
                                    const leave = getLeaveForDay(employee.id, day);
                                    const holidayName = getPublicHolidayName(day);

                                    return (
                                        <td
                                            key={day}
                                            className={`day-cell ${leave ? 'leave-cell' : ''} ${
                                                isWeekend(day) ? 'weekend' : ''
                                            } ${isPublicHoliday(day) ? 'public-holiday' : ''}`}
                                            title={holidayName}
                                        >
                                            {leave && (
                                                <div
                                                    className={`leave-indicator ${
                                                        leave.request_unit_half
                                                            ? `half-day ${leave.request_date_from_period}`
                                                            : ''
                                                    }`}
                                                    style={
                                                        {
                                                            '--leave-color': getLeaveTypeColor(
                                                                leave.holiday_status_id[0]
                                                            ),
                                                            backgroundColor: leave.request_unit_half
                                                                ? 'transparent'
                                                                : getLeaveTypeColor(leave.holiday_status_id[0]),
                                                        } as React.CSSProperties
                                                    }
                                                    title={`${getLeaveTypeFullLabel(leave.holiday_status_id[0])}${
                                                        leave.request_unit_half
                                                            ? ` (${
                                                                  leave.request_date_from_period === 'am'
                                                                      ? 'Matin'
                                                                      : 'Après-midi'
                                                              })`
                                                            : ''
                                                    }`}
                                                >
                                                    {getLeaveTypeLabel(leave.holiday_status_id[0])}
                                                </div>
                                            )}
                                        </td>
                                    );
                                })}
                                <td className="employee-stats">
                                    {Object.entries(calculateEmployeeStats(employee.id)).map(([code, count]) => {
                                        const leaveType = leaveTypes.find(
                                            (type) => type.calendar_abbreviation === code
                                        );
                                        return (
                                            <div
                                                key={code}
                                                className="stat-value"
                                                style={{ backgroundColor: leaveType?.calendar_color || '#607d8b' }}
                                            >
                                                {count}
                                            </div>
                                        );
                                    })}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        {/* Totals row */}
                        <tr className="totals-row">
                            <td className="employee-name">Total</td>
                            {days.map((day) => (
                                <td key={day} className="day-cell" />
                            ))}
                            <td className="employee-stats">
                                {Object.entries(calculateTotalStats(permanentEmployees)).map(([code, total]) => {
                                    const leaveType = leaveTypes.find((type) => type.calendar_abbreviation === code);
                                    return (
                                        <div
                                            key={code}
                                            className="stat-value"
                                            style={{ backgroundColor: leaveType?.calendar_color || '#607d8b' }}
                                        >
                                            {total}
                                        </div>
                                    );
                                })}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
};

const ToolbarCustom: React.FC<{
    date: DateTime;
    setDate: (date: DateTime) => void;
}> = ({ date, setDate }) => {
    const { data: leaveTypes } = useLeaveTypes();

    return (
        <div className="planning-toolbar">
            <div className="nav-buttons">
                <button onClick={() => setDate(date.minus({ months: 1 }))}>
                    <IonIcon icon={arrowBack} />
                </button>
                <button onClick={() => setDate(DateTime.now())}>
                    <IonIcon icon={today} />
                </button>
                <button onClick={() => setDate(date.plus({ months: 1 }))}>
                    <IonIcon icon={arrowForward} />
                </button>
            </div>
            <div className="leave-legend">
                {leaveTypes.map((type) => (
                    <div key={type.id} className="legend-item">
                        <div className="color-box" style={{ backgroundColor: type.calendar_color }} />
                        <span>{type.name}</span>
                    </div>
                ))}
            </div>
            <div className="nav-buttons">
                <button onClick={() => window.print()}>
                    <IonIcon icon={download} />
                </button>
            </div>
        </div>
    );
};

export default LeaveCalendar;
